import { axiosInstance } from "./axios-config";
import { AxiosPromise, AxiosRequestConfig } from "axios";

import { makeUseAxios, RefetchOptions, ResponseValues } from "axios-hooks";
import { useCallback, useMemo } from "react";
import { ErrorResponse } from "../../common/api/Models/ErrorResponse";

export const useAxios = makeUseAxios({ axios: axiosInstance });

export type UseAxiosActionResult<TRequest, TResponse, TError = ErrorResponse> = [
	ResponseValues<TResponse, unknown, TError>,
	(requestData: TRequest, config?: AxiosRequestConfig, options?: RefetchOptions) => AxiosPromise<TResponse>,
	() => void,
	AxiosRequestConfig
];

/**
 * Axios hook for GET request. useAxiosGet(url|config, [manual], [useCache]).
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 * @remarks manual - If true, the request will not be made automatically on component mount. Default is false (request will be made automatically).
 * @remarks useCache - If true, the hook will return cached response if the same request was made before. Default is true (use caching).
 */
export function useAxiosGet<TResponse, TError = ErrorResponse>(config: string | AxiosRequestConfig, manual?: boolean, useCache?: boolean) {
	const _config = useAxiosConfig(config, "GET");
	return useAxios<TResponse, TError>(_config, { manual, autoCancel: false, useCache });
}

/**
 * Axios hook for POST request. useAxiosPost(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosPost<TRequest, TResponse, TError = ErrorResponse>(
	config: string | AxiosRequestConfig,
	manual?: boolean
) {
	return useAxiosAction<TRequest, TResponse, TError>(config, "POST", manual);
}

/**
 * Axios hook for PUT request. useAxiosPut(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosPut<TRequest, TResponse, TError = ErrorResponse>(
	config: string | AxiosRequestConfig,
	manual?: boolean
) {
	return useAxiosAction<TRequest, TResponse, TError>(config, "PUT", manual);
}

/**
 * Axios hook for DELETE request. useAxiosDelete(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosDelete<TRequest, TResponse, TError = ErrorResponse>(config: string | AxiosRequestConfig) {
	return useAxiosAction<TRequest, TResponse, TError>(config, "DELETE");
}

/**
 * Axios hook for PATCH request. useAxiosDelete(url|config)
 * For axios-hooks documentation, please go to https://github.com/simoneb/axios-hooks
 * @remarks url|config - The request URL or config object, the same argument accepted by axios
 */
export function useAxiosPatch<TRequest, TResponse, TError = ErrorResponse>(config: string | AxiosRequestConfig) {
	return useAxiosAction<TRequest, TResponse, TError>(config, "PATCH");
}

function useAxiosAction<TRequest, TResponse, TError = ErrorResponse>(
	config: string | AxiosRequestConfig,
	method: "POST" | "PUT" | "DELETE" | "PATCH",
	manual = true
): UseAxiosActionResult<TRequest, TResponse, TError> {
	const _config = useAxiosConfig(config, method);
	const [state, execute, manualCancel] = useAxios<TResponse, TError>(_config, {
		manual,
		autoCancel: false,
	});

	const actionAsync = useCallback(
		async (model: TRequest, requestConfig?: AxiosRequestConfig) => {
			const requestModel = typeof model === "string" ? `"${model}"` : model;
			return await execute({ ...requestConfig, data: requestModel } as AxiosRequestConfig);
		},
		[execute]
	);

	return useMemo(() => [state, actionAsync, manualCancel, _config], [_config, actionAsync, manualCancel, state]);
}

function useAxiosConfig(
	config: string | AxiosRequestConfig,
	method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
): AxiosRequestConfig {
	return useMemo(() => {
		if (typeof config === "string") {
			return {
				url: config,
				method,
			};
		}
		config.method = method;
		return config;
	}, [config, method]);
}
